import moment from 'moment';
import * as PropTypes from 'prop-types';
import React from 'react';
import { compose } from 'redux';
import {
  fetchEnd,
  fetchStart,
  GET_ONE,
  Pagination,
  ReferenceManyField,
  UPDATE,
  GET_MANY_REFERENCE,
  showNotification,
  translate as translateHOC,
  withDataProvider,
  refreshView,
} from 'react-admin';
import { connect } from 'react-redux';
import { InvoicesFields } from '../../../core/data-fields/data-invoices';
import CustomDatagrid from '../../../shared/custom-datagrid';
import withGrants from '../../WithGrants';
import Header from '../../../shared/header';

import InvoiceFilters from './invoiceFilters';
import RefundByItemDialog from '../../refund-by-item/RefundByItemDialog';
import RefundNoteViewerDialog from '../../refund-by-item/RefundNoteViewerDialog';
import CancelDialog from './CancelDialog';
import SetAsPaidDialog from './SetAsPaidDialog';
import { calculateTimeDifferenceInDays } from '../../../shared/utils/date';
import { formatInvoicesCSVFile } from '../../../helpers/invoiceHelpers';
import { getBusinessConf } from '../../../core/config';
import { downloadFileWithDate } from '../../../helpers';

class InvoiceList extends React.PureComponent {
  pagination = React.createRef();

  defaultDates = {
    startDate: moment()
      .subtract(3, 'month')
      .format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  };

  state = {
    ...this.state,
    filterValues: this.defaultDates,
    backendFilter: this.defaultDates,
    perPage: 10,
    isSetAsPaidDialogOpen: false,
    isRefundDialogOpen: false,
    isCancelDialogOpen: false,
    invoice: {},
    invoiceId: '',
    // Refund-by-item dialog state (ISJ-9021): items === null while the refundable
    // amount is loading; submitted holds the FE-built receipt after an async POST.
    refundItems: null,
    refundableAmount: 0,
    refundableByRate: [],
    refundCurrency: 'EUR',
    isLoadingRefundItems: false,
    isSubmittingRefund: false,
    refundSubmitted: null,
    isRefundNoteOpen: false,
    refundNoteInvoiceId: '',
    isFilteredDatesOverLimit: false,
  };

  componentDidUpdate() {
    const { filterValues, backendFilter } = this.state;

    const { startDate, endDate } = filterValues;

    const timeDifferenceInDays = calculateTimeDifferenceInDays(new Date(startDate), new Date(endDate));

    const timeLimitInDays = 365;

    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({ isFilteredDatesOverLimit: timeDifferenceInDays > timeLimitInDays });

    if (
      filterValues.startDate !== '' &&
      filterValues.endDate !== '' &&
      (backendFilter.startDate !== filterValues.startDate || backendFilter.endDate !== filterValues.endDate) &&
      timeDifferenceInDays < timeLimitInDays
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        backendFilter: {
          ...backendFilter,
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
        },
      });
    }
  }

  handleChangePage = value => {
    this.pagination.current.props.setPage(value);
  };

  setFilters = (name, value) => {
    const { filterValues } = this.state;
    const newFilter = {
      ...filterValues,
      [name]: value,
    };
    this.setState({ filterValues: newFilter });
  };

  resetDates = () => {
    this.setState({ filterValues: this.defaultDates });
  };

  downloadPdf = invoiceId => {
    const { dispatch, dataProvider } = this.props;
    dispatch(fetchStart());
    dataProvider(GET_ONE, 'businessInvoiceDownload', { field: 'invoice', extension: 'PDF', invoiceId })
      .then(({ data }) => {
        // It is necessary to create a new blob object with mime-type explicitly set
        // otherwise only Chrome works like it should
        const newBlob = new Blob([data], { type: 'application/pdf' });

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(newBlob);
          return;
        }

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const objectUrl = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = 'invoice.pdf';
        document.body.appendChild(link);
        link.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(data);
        }, 100);
      })
      .catch(err => {
        if (typeof err.message === 'object') {
          const errObject = JSON.parse(err.message);
          const { errorCode, httpStatusCode, httpStatus } = errObject;

          console.error(`Error: ${httpStatusCode}`, {
            httpStatusCode,
            httpStatus,
            errorCode,
          });
          dispatch(showNotification(`error.${errorCode}`, 'warning'));
        } else if (typeof err.message === 'string') {
          dispatch(showNotification(err.message, 'warning'));
        }
      })
      .finally(dispatch(fetchEnd()));
  };

  // Download the B2B credit note (avoir) of a cancelled business invoice. Guards against a
  // non-PDF payload (e.g. a JSON error surfaced as a blob when the credit note is not yet
  // generated) so we never save an empty or corrupted file, and surfaces an explicit error.
  downloadCreditNote = invoiceId => {
    const { dispatch, dataProvider } = this.props;
    dispatch(fetchStart());
    dataProvider(GET_ONE, 'businessInvoiceDownload', { field: 'creditNote', invoiceId })
      .then(({ data }) => {
        const isValidPdf = data && data.size > 0 && (!data.type || data.type === 'application/pdf');
        if (!isValidPdf) {
          dispatch(showNotification('notification.invoices.creditNoteUnavailable', 'warning'));
          return;
        }

        const newBlob = new Blob([data], { type: 'application/pdf' });

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(newBlob);
          return;
        }

        const objectUrl = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = 'credit-note.pdf';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(objectUrl);
        }, 100);
      })
      .catch(err => {
        console.error('[downloadCreditNote]', err);
        dispatch(showNotification('notification.invoices.creditNoteUnavailable', 'warning'));
      })
      .finally(() => dispatch(fetchEnd()));
  };

  downloadCSV = async ({ invoiceId, invoiceType, currency, invoiceDate }) => {
    const { dispatch, dataProvider, id: entityId, locale, timezone, entityData } = this.props;
    const { config } = getBusinessConf();
    dispatch(fetchStart());

    try {
      // Get config for CSV formatting
      const invoiceFileConfig =
        invoiceType === 'TRIP'
          ? config && config.invoiceFile && config.invoiceFile.CSV && config.invoiceFile.CSV.trips
          : config && config.invoiceFile && config.invoiceFile.CSV && config.invoiceFile.CSV.products;
      const query = new URLSearchParams();

      if (invoiceFileConfig && Array.isArray(invoiceFileConfig.columns) && invoiceFileConfig.columns.length > 0) {
        query.append('columns', invoiceFileConfig.columns.map(column => column.name).join(','));
      }

      const response = await dataProvider(GET_ONE, 'businessInvoiceDownload', {
        field: 'invoice',
        extension: 'CSV',
        invoiceId,
        invoiceType,
        entityId,
        ...(query.size > 0 && { query }),
      });

      // Convert response.data to proper text format
      let csvText;

      // Handle different response formats
      if (response.data instanceof Blob) {
        csvText = await response.data.text();
      } else if (typeof response.data === 'string') {
        csvText = response.data;
      } else {
        throw new Error('Unexpected response format');
      }

      // Create a new blob from the CSV text
      const blob = formatInvoicesCSVFile(
        csvText, // Pass the raw CSV text string
        {
          config: invoiceFileConfig,
          currency,
          locale: locale && locale.replace('_', '-'),
          timezone,
        }
      );

      // Generate the prefix for the filename
      const entityName = entityData && entityData[entityId] && entityData[entityId].name;
      const getPrefix = () => {
        const result = [];
        const prefix = invoiceFileConfig && invoiceFileConfig.fileName && invoiceFileConfig.fileName.prefix;
        const insertEntityName =
          invoiceFileConfig && invoiceFileConfig.fileName && invoiceFileConfig.fileName.insertEntityName;

        if (!prefix && !insertEntityName) return 'Invoice';

        if (invoiceFileConfig && invoiceFileConfig.fileName && invoiceFileConfig.fileName.prefix) {
          result.push(invoiceFileConfig.fileName.prefix);
        }

        if (
          invoiceFileConfig &&
          invoiceFileConfig.fileName &&
          invoiceFileConfig.fileName.insertEntityName &&
          entityName
        ) {
          result.push(entityName.replace(' ', '_'));
        }

        return result.join('-');
      };

      if (invoiceDate && blob) {
        downloadFileWithDate(blob, {
          date: invoiceDate,
          extension: 'csv',
          prefix: getPrefix(),
          timezone,
        });
      } else {
        console.error('[SAGA][downloadBusinessInvoiceCSVRequest]: Information missing for invoice creation');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      dispatch(showNotification('notification.invoices.downloadCSVFailure', 'warning'));
    } finally {
      dispatch(fetchEnd());
    }
  };

  fetchInvoices = page => {
    const { filterValues, perPage } = this.state;
    const { dataProvider, id, timezone } = this.props;
    const filter = { ...filterValues, timezone };

    dataProvider(GET_MANY_REFERENCE, 'businessInvoice', {
      id,
      filter,
      pagination: { perPage, page: page || 1 },
      sort: 'invoiceDate',
    });
  };

  openSetAsPaidDialog = invoiceId => {
    this.toggleSetAsPaidDialog();
    this.setState({ invoiceId });
  };

  toggleSetAsPaidDialog = () => {
    this.setState(({ isSetAsPaidDialogOpen }) => ({ isSetAsPaidDialogOpen: !isSetAsPaidDialogOpen }));
  };

  setInvoiceToPending = async invoiceId => {
    const { dataProvider, dispatch } = this.props;
    try {
      await dataProvider(UPDATE, 'businessInvoice', { actionType: 'updateStatus', invoiceId, status: 'PENDING' });
      dispatch(refreshView());
      dispatch(showNotification('notification.invoices.setToPendingSuccessfully'));
    } catch (err) {
      console.error(err);
      dispatch(showNotification('notification.invoices.setToPendingFailure', 'warning'));
    }
  };

  openCancelDialog = invoice => {
    this.toggleCancelDialog();
    this.setState({ invoice });
  };

  toggleCancelDialog = () => {
    const { isCancelDialogOpen } = this.state;
    this.setState({ isCancelDialogOpen: !isCancelDialogOpen });
  };

  onCancel = () => {
    const { dataProvider, dispatch } = this.props;
    const { invoice } = this.state;
    dataProvider(UPDATE, 'businessInvoice', { actionType: 'updateStatus', invoiceId: invoice.id, status: 'CANCELLED' })
      .then(() => {
        dispatch(showNotification('notification.invoices.canceledSuccessfully'));
      })
      .catch(e => {
        console.error(e);
        dispatch(
          showNotification('notification.invoices.canceledFailure', 'info', { messageArgs: { error_message: e } })
        );
      })
      .finally(() => {
        this.toggleCancelDialog();
        dispatch(refreshView());
      });
  };

  openRefundDialog = invoice => {
    const { dataProvider, dispatch } = this.props;
    this.setState({
      isRefundDialogOpen: true,
      invoice,
      refundItems: null,
      refundableAmount: 0,
      refundableByRate: [],
      refundCurrency: invoice.currency || 'EUR',
      isLoadingRefundItems: true,
      isSubmittingRefund: false,
      refundSubmitted: null,
    });
    dataProvider(GET_ONE, 'businessInvoice', { field: 'getRefundableAmount', invoiceId: invoice.id })
      .then(({ data }) => {
        this.setState({
          refundItems: Array.isArray(data.refundableItems) ? data.refundableItems : [],
          refundableAmount: data.refundableAmount,
          refundableByRate: data.refundableByRate || [],
          isLoadingRefundItems: false,
        });
      })
      .catch(e => {
        console.error(`Error: ${e}`);
        // Close the dialog on a load failure so the operator can retry, rather than dropping
        // into the empty state (which would read as "nothing to refund").
        this.setState({ isRefundDialogOpen: false, isLoadingRefundItems: false });
        dispatch(showNotification('notification.invoices.refundedFailure', 'warning'));
      });
  };

  closeRefundDialog = () => {
    const { dispatch } = this.props;
    const { refundSubmitted } = this.state;
    this.setState({ isRefundDialogOpen: false });
    // Refetch after a completed submit so the row reflects the true backend status.
    if (refundSubmitted) dispatch(refreshView());
  };

  onRefund = ({ note, refundItems, receipt }) => {
    const { dataProvider, dispatch } = this.props;
    const { invoice } = this.state;
    this.setState({ isSubmittingRefund: true });
    dataProvider(GET_ONE, 'businessInvoice', {
      field: 'refund',
      invoiceId: invoice.id,
      payload: { note, refundItems },
    })
      .then(() => {
        // The POST is async (no body); refetch on close reflects the true status.
        this.setState({ isSubmittingRefund: false, refundSubmitted: receipt || {} });
        dispatch(showNotification('notification.invoices.refundedSuccessfully'));
      })
      .catch(e => {
        console.error(e);
        this.setState({ isSubmittingRefund: false });
        dispatch(
          showNotification('notification.invoices.refundedFailure', 'warning', { messageArgs: { error_message: e } })
        );
      });
  };

  openRefundNote = invoiceId => {
    this.setState({ isRefundNoteOpen: true, refundNoteInvoiceId: invoiceId });
  };

  closeRefundNote = () => {
    this.setState({ isRefundNoteOpen: false });
  };

  // Fetch the refund-note PDF as a blob for the in-app viewer (bearer auth can't ride
  // on an <iframe src>, so the viewer consumes this blob via a URL.createObjectURL).
  loadRefundNotePdf = () => {
    const { dataProvider } = this.props;
    const { refundNoteInvoiceId } = this.state;
    return dataProvider(GET_ONE, 'businessInvoiceDownload', {
      field: 'refundNote',
      invoiceId: refundNoteInvoiceId,
    }).then(({ data }) => new Blob([data], { type: 'application/pdf' }));
  };

  render() {
    const { translate, id, permissions, checkPermission, timezone, dispatch, showUnitInvoices, ...props } = this.props;
    const {
      filterValues,
      currentSelection,
      perPage,
      isSetAsPaidDialogOpen,
      isCancelDialogOpen,
      isRefundDialogOpen,
      invoice,
      invoiceId,
      refundItems,
      refundableAmount,
      refundableByRate,
      refundCurrency,
      isLoadingRefundItems,
      isSubmittingRefund,
      refundSubmitted,
      isRefundNoteOpen,
      backendFilter,
      isFilteredDatesOverLimit,
    } = this.state;
    const { entityData } = this.props;
    const {
      downloadPdf,
      downloadCSV,
      setFilters,
      resetDates,
      openSetAsPaidDialog,
      toggleSetAsPaidDialog,
      openCancelDialog,
      toggleCancelDialog,
      onCancel,
      openRefundDialog,
      closeRefundDialog,
      onRefund,
      openRefundNote,
      closeRefundNote,
      loadRefundNotePdf,
      downloadCreditNote,
      setInvoiceToPending,
    } = this;
    const entityName = entityData && entityData[id] && entityData[id].name;
    const invoiceLabel = invoice && invoice.sequence ? `#${invoice.sequence}` : '';
    const record = { id };
    const filter = { ...backendFilter, timezone };

    const datagridProps = { ...this.props };
    [
      'classes',
      'translate',
      'downloadPdf',
      'downloadCSV',
      'dataProvider',
      'dispatch',
      'entityData',
      'showUnitInvoices',
    ].forEach(propName => {
      delete datagridProps[propName];
    });

    const invoiceFieldsToRender = {
      translate,
      downloadPdf,
      downloadCSV,
      openCancelDialog,
      openRefundDialog,
      openRefundNote,
      downloadCreditNote,
      setInvoiceToPending,
      permissions,
      checkPermission,
      openSetAsPaidDialog,
    };

    return (
      <>
        <InvoiceFilters
          translate={translate}
          filterValues={filterValues}
          setFilters={setFilters}
          resetDates={resetDates}
          isFilteredDatesOverLimit={isFilteredDatesOverLimit}
          periodLimit={3}
        />
        <Header label={showUnitInvoices ? 'menu.accumulativeInvoices' : 'menu.invoice'} />
        <ReferenceManyField
          {...props}
          pagination={<Pagination ref={this.pagination} rowsPerPageOptions={[10, 25, 50]} />}
          perPage={perPage}
          record={record}
          filter={filter}
          reference="businessInvoice"
          target="id"
          currentSelection={currentSelection}
          sort={{ field: 'invoiceDate', order: 'DESC' }}
        >
          <CustomDatagrid
            {...datagridProps}
            onColumnChange={selection => {
              this.setState({ currentSelection: selection });
            }}
            defaultColumns={['total', 'type', 'status', 'downloadButton']}
            id="Selenium-Business-Invoices-Table"
          >
            {InvoicesFields(invoiceFieldsToRender)}
          </CustomDatagrid>
        </ReferenceManyField>
        <RefundByItemDialog
          open={isRefundDialogOpen}
          variant="cumulative"
          invoiceId={invoice && invoice.id}
          invoiceLabel={invoiceLabel}
          entityName={entityName}
          pspReference={invoice && invoice.pspReference}
          currency={refundCurrency}
          items={refundItems}
          refundableAmount={refundableAmount}
          refundableByRate={refundableByRate}
          loading={isLoadingRefundItems}
          submitting={isSubmittingRefund}
          submitted={refundSubmitted}
          onClose={closeRefundDialog}
          onSubmit={onRefund}
        />
        <RefundNoteViewerDialog open={isRefundNoteOpen} loadPdf={loadRefundNotePdf} onClose={closeRefundNote} />
        <CancelDialog
          isOpen={isCancelDialogOpen}
          toggleDialog={toggleCancelDialog}
          invoice={invoice}
          onCancel={onCancel}
          translate={translate}
        />
        <SetAsPaidDialog
          isOpen={isSetAsPaidDialogOpen}
          toggleDialog={toggleSetAsPaidDialog}
          invoiceId={invoiceId}
          translate={translate}
          dispatch={dispatch}
        />
      </>
    );
  }
}

InvoiceList.propTypes = {
  dataProvider: PropTypes.func,
  dispatch: PropTypes.func,
  translate: PropTypes.func,
  id: PropTypes.string,
  checkPermission: PropTypes.func,
  timezone: PropTypes.string,
  permissions: PropTypes.string,
  showUnitInvoices: PropTypes.bool,
  locale: PropTypes.string,
  entityData: PropTypes.shape({}),
};

const mapStateToProps = state => ({
  timezone: state.configuration.fleet.timezone,
  locale: state.i18n.locale,
  entityData: state.admin.resources.business.data,
});

export default compose(
  withGrants,
  withDataProvider,
  translateHOC,
  connect(mapStateToProps)
)(InvoiceList);

// For Tests
export const InvoiceListTest = InvoiceList;
